/**
 * Sport-clubs API.
 * Slim catalog: `GET /foot-clubs`. CRUD: `/partners/sport-clubs` (backend prefix).
 */

import { apiClient } from './api-client';
import type {
  CreateSportClubDto,
  ListSportClubsParams,
  SportClub,
  SportClubOption,
  UpdateSportClubDto,
} from '@/types/sport-club.types';
import type { PaginatedResponse } from '@/types/data-table.types';

/** Backend CRUD prefix (Nest mounts sport-clubs under `/partners`). */
const SPORT_CLUBS_BASE = '/partners/sport-clubs';

/** `GET /foot-clubs` — slim list for selects. */
export const getSportClubs = async (): Promise<SportClubOption[]> => {
  const { data } = await apiClient.get<SportClubOption[]>('/foot-clubs');
  return data;
};

/** `GET /partners/sport-clubs` — paginated sport clubs. */
export const listSportClubs = async (
  params?: ListSportClubsParams,
): Promise<PaginatedResponse<SportClub>> => {
  const response = await apiClient.get<PaginatedResponse<SportClub>>(SPORT_CLUBS_BASE, {
    params,
  });
  return response.data;
};

/** `GET /partners/sport-clubs/:id` */
export const getSportClubById = async (id: string): Promise<SportClub> => {
  const response = await apiClient.get<SportClub>(`${SPORT_CLUBS_BASE}/${id}`);
  return response.data;
};

/** `POST /partners/sport-clubs` */
export const createSportClub = async (data: CreateSportClubDto): Promise<SportClub> => {
  const response = await apiClient.post<SportClub>(SPORT_CLUBS_BASE, data);
  return response.data;
};

/** `PUT /partners/sport-clubs/:id` */
export const updateSportClub = async (
  id: string,
  data: UpdateSportClubDto,
): Promise<SportClub> => {
  const response = await apiClient.put<SportClub>(`${SPORT_CLUBS_BASE}/${id}`, data);
  return response.data;
};

/** `DELETE /partners/sport-clubs/:id` */
export const deleteSportClub = async (id: string): Promise<void> => {
  await apiClient.delete(`${SPORT_CLUBS_BASE}/${id}`);
};

/** `GET /partners/sport-clubs/export-excel` — download XLSX. */
export const exportSportClubsExcel = async (params?: ListSportClubsParams): Promise<void> => {
  const response = await apiClient.get(`${SPORT_CLUBS_BASE}/export-excel`, {
    params,
    responseType: 'blob',
  });
  const url = URL.createObjectURL(response.data as Blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `clubs-de-sport-${new Date().toISOString().slice(0, 10)}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};
